<script setup lang="ts">
import type { UseFieldEmits, UIInputBaseProps } from '../../types'
import { nextTick, onBeforeUnmount, onMounted, ref, useCoreConfig, useCoreLogger, useField, useI18n, useRoute, useTracking, watch } from '#imports'
import { useTurnstile } from '../../composables/useTurnstile'
import { addConsoleListener, type ConsoleListenerMethod } from '../../utils/consoleListeners'

export interface TurnstileInputProps extends UIInputBaseProps {
    modelValue?: string
    theme?: 'light' | 'dark'
    appearance?: 'always' | 'interaction-only'
    validationStrategy?: 'immediate' | 'lazy'
}

const props = withDefaults(
    defineProps<TurnstileInputProps>(),
    {
        theme: 'light',
        appearance: 'interaction-only',
        validationStrategy: 'immediate'
    }
)
const emit = defineEmits<UseFieldEmits>()

const { locale, security: { turnstile: turnstileConfig } } = useCoreConfig()
const { t } = useI18n()

const logger = useCoreLogger()

if (!turnstileConfig?.siteKey) {
    logger.warn('UITurnstileInput is disabled, since NUXT_PUBLIC_CORE_SECURITY_TURNSTILE_SITE_KEY is missing')
    throw new Error('UITurnstileInput is missing NUXT_PUBLIC_CORE_SECURITY_TURNSTILE_SITE_KEY')
}

const { trackEvent } = useTracking()
const route = useRoute()

const script = useTurnstile()

const widgetId = ref<string>()
const wasInteractive = ref(false)
const inputContainerRef = ref<HTMLDivElement>()
const status = ref<'pending' | 'interactive' | 'success' | 'error'>('pending')
if (props.validationStrategy === 'lazy') {
    watch(status, async (status) => {
        if (!['interactive', 'error'].includes(status)) return

        await nextTick()

        // Re-run rules manually
        context.validate({ mode: 'force' })
    })
}

const { context } = useField({
    props: () => {
        if (props.validationStrategy === 'immediate') return props

        // Don't add rules when validationStrategy is lazy, since we want the turnstile to try completing in the background.
        const preparedProps = { ...props }

        if (status.value === 'pending') {
            preparedProps.rules = undefined
        }

        return preparedProps
    },
    emit,
    on: {
        enableInput: true,
        enableChange: true,
    },
    options: {
        initialValue: props.modelValue,
        type: 'default',
    }
})

function render() {
    script.onLoaded(({ turnstile }) => {
        if (widgetId.value) {
            turnstile.reset(widgetId.value)
            return
        }

        widgetId.value = turnstile.render(inputContainerRef.value!, {
            'sitekey': turnstileConfig!.siteKey,
            'language': locale.split('-')[0],
            'callback': function (token: string) {
                context.resetField()
                context.setValue(token)
                status.value = 'success'
                if (wasInteractive.value) {
                    trackEvent({
                        event_name: 'turnstile_status',
                        event_type: 'Click',
                        meta_data: [{ category: 'Turnstile', status: 'success', auth: route.query.auth }],
                    })
                }
            },
            'error-callback': function (errorCode: string) {
                context.setErrors(`${t('core.defaultErrorMessage')} (${errorCode})`)
                status.value = 'error'
                trackEvent({
                    event_name: 'turnstile_status',
                    event_type: 'Click',
                    meta_data: [{ category: 'Turnstile', status: 'error', errorCode, auth: route.query.auth }],
                })
                return true
            },
            'before-interactive-callback': function () {
                status.value = 'interactive'
                wasInteractive.value = true
                trackEvent({
                    event_name: 'turnstile_status',
                    event_type: 'Click',
                    meta_data: [{ category: 'Turnstile', status: 'interactive', auth: route.query.auth }],
                })
            }
        })

        const turnstileDiv = inputContainerRef.value?.querySelector('div')
        if (turnstileDiv) {
            turnstileDiv.setAttribute('tabindex', '-1')
        }
    })
}

function reset() {
    context.resetField()
    status.value = 'pending'
    wasInteractive.value = false

    if (!widgetId.value) return

    script.onLoaded(({ turnstile }) => {
        turnstile.reset(widgetId.value!)
    })
}

const maxConsoleMessageLength = 500
const trackedConsoleMessages = new Set<string>()
let isTrackingConsole = false
let removeConsoleListener: (() => void) | undefined

function stringifyConsoleArg(arg: unknown) {
    if (arg instanceof Error) return arg.message
    if (typeof arg !== 'object' || arg === null) return String(arg)

    try {
        return JSON.stringify(arg) ?? String(arg)
    }
    catch {
        return String(arg)
    }
}

// The Turnstile script reports why it fails (e.g. in Android WebView) only via console warnings/errors
function trackConsole(method: ConsoleListenerMethod, args: unknown[]) {
    // trackEvent may log synchronously, which would re-enter here; later async logs are bounded by the dedupe
    if (isTrackingConsole) return

    // The patch sees every console call on the page, so filter on the first arg before stringifying anything
    const [first, ...rest] = args
    if (typeof first !== 'string' || !/turnstile/i.test(first)) return

    const message = [first, ...rest.map(stringifyConsoleArg)].join(' ').slice(0, maxConsoleMessageLength)
    if (trackedConsoleMessages.has(message)) return

    trackedConsoleMessages.add(message)
    isTrackingConsole = true
    trackEvent({
        event_name: 'turnstile_status',
        event_type: 'Click',
        meta_data: [{ category: 'Turnstile', status: `console_${method}`, message, auth: route.query.auth }],
    }).catch(() => {}) // A failed tracking call must not surface as an unhandled rejection
    isTrackingConsole = false
}

onMounted(async () => {
    removeConsoleListener = addConsoleListener(trackConsole)

    // Reset field to ensure users are not allowed to send an outdated token in case they are going back and forth between steps in e.g. signup
    if (props.modelValue) {
        context.resetField()
        context.setValue(undefined)
    }

    render()
})

onBeforeUnmount(() => {
    removeConsoleListener?.()

    if (widgetId.value) {
        script.onLoaded(({ turnstile }) => {
            turnstile.remove(widgetId.value!)
        })
    }
})

defineExpose({
    status,
    reset
})
</script>

<template>
    <div class="turnstile-input">
        <div
            ref="inputContainerRef"
            :data-theme="props.theme"
            data-size="flexible"
            data-feedback-enabled="false"
            :data-appearance="props.appearance"
        />

        <UIErrorMessages :messages="context.errors.value" :data-test="props.dataTest" />
    </div>
</template>
